import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Edges, Text } from '@react-three/drei';
import gsap from 'gsap';

const INK = '#171113';
const PAPER = '#f4f0f2';
const ACCENT = '#751628';
const MUTED = '#8c7b80';

const ProjectTile = ({
  project,
  index,
  position,
  scale,
  rotation,
  onClick,
  isPhone,
  focused,
  dimmed,
  isActive,
}) => {
  const projectRef = useRef(null);
  const cardRef = useRef(null);
  const hitBoxRef = useRef(null);
  const accentRef = useRef(null);
  const shadowRef = useRef(null);
  const titleRef = useRef(null);
  const metaRef = useRef(null);
  const detailsRef = useRef(null);
  const buttonRef = useRef(null);
  const hoverAnimRef = useRef(null);
  const entranceAnimRef = useRef(null);
  const pointerStartRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const expanded = isActive && (hovered || (isPhone && focused));

  const reducedMotion = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    []
  );

  const titleProps = useMemo(() => ({
    font: '/soria-font.ttf',
    color: INK,
    anchorX: 'left',
    ...(isPhone ? {
      sdfGlyphSize: 128,
      fillOpacity: dimmed ? 0.62 : 1,
    } : {}),
  }), [dimmed, isPhone]);

  const bodyProps = useMemo(() => ({
    font: '/Vercetti-Regular.woff',
    color: INK,
    anchorX: 'left',
    anchorY: 'top',
    ...(isPhone ? {
      sdfGlyphSize: 128,
      fillOpacity: dimmed ? 0.62 : 1,
    } : {}),
  }), [dimmed, isPhone]);

  const tags = useMemo(
    () => (project.stack || []).slice(0, 3),
    [project.stack]
  );

  useEffect(() => {
    if (!projectRef.current) return undefined;

    hoverAnimRef.current?.kill();
    const duration = reducedMotion || !isActive ? 0 : expanded ? 0.28 : 0.2;
    const ease = expanded ? 'power3.out' : 'power2.in';
    const expandedCardScale = isPhone ? 2.0 : 2.05;
    const expandedGroupScale = isPhone ? 1.18 : 1.12;
    const restingGroupScale = dimmed ? 0.82 : 1;
    const phoneFocusedY = position[1] > 2 ? -2.85 : 1.05;

    hoverAnimRef.current = gsap.timeline({ defaults: { duration, ease } })
      .to(projectRef.current.position, {
        z: expanded ? (isPhone ? 1.8 : 1.1) : (dimmed ? -0.65 : 0),
        y: expanded ? (isPhone ? phoneFocusedY : 0.18) : 0,
      }, 0)
      .to(projectRef.current.scale, {
        x: expanded ? expandedGroupScale : restingGroupScale,
        y: expanded ? expandedGroupScale : restingGroupScale,
        z: expanded ? expandedGroupScale : restingGroupScale,
      }, 0)
      .to(cardRef.current.scale, { y: expanded ? expandedCardScale : 1 }, 0)
      .to(hitBoxRef.current.scale, { y: expanded ? expandedCardScale : 1 }, 0)
      .to(cardRef.current.position, { y: expanded ? (isPhone ? 0 : 1.0) : 0 }, 0)
      .to(hitBoxRef.current.position, { y: expanded ? (isPhone ? 0 : 1.0) : 0 }, 0)
      .to(accentRef.current.scale, {
        y: expanded && isPhone ? expandedCardScale : 1,
      }, 0)
      .to(shadowRef.current.scale, { y: expanded ? expandedCardScale : 1 }, 0)
      .to(shadowRef.current.position, {
        y: expanded ? (isPhone ? -0.08 : 0.9) : -0.08,
      }, 0)
      .to(titleRef.current.position, { y: expanded ? (isPhone ? 0.55 : 1.52) : -0.62 }, 0)
      .to(metaRef.current.position, { y: expanded ? (isPhone ? 2.02 : 2.55) : 0.82 }, 0)
      .to(detailsRef.current.position, { y: expanded ? (isPhone ? -0.03 : 0.65) : 0.25 }, 0)
      .to(detailsRef.current, { visible: expanded }, 0)
      .to(detailsRef.current.scale, {
        x: expanded ? 1 : 0.96,
        y: expanded ? 1 : 0.96,
        z: 1,
      }, 0)
      .to(buttonRef.current.scale, {
        x: expanded ? 1 : 0,
        y: expanded ? 1 : 0,
        z: 1,
      }, 0)
      .to(buttonRef.current.position, {
        x: expanded ? (isPhone ? 1.25 : 1.25) : 1.25,
        y: expanded ? (isPhone ? 1.58 : 1.58) : -0.74,
        z: expanded ? 0.12 : -0.4,
      }, 0);

    return () => hoverAnimRef.current?.kill();
  }, [dimmed, expanded, isActive, isPhone, position, reducedMotion]);

  useEffect(() => {
    if (isPhone || !isActive) setHovered(false);
  }, [isActive, isPhone]);

  useEffect(() => {
    if (!projectRef.current) return undefined;

    entranceAnimRef.current?.kill();

    if (!isActive) {
      gsap.set(projectRef.current.position, { y: 0 });
      return undefined;
    }

    entranceAnimRef.current = gsap.fromTo(
      projectRef.current.position,
      { y: reducedMotion ? 0 : -7 },
      {
        y: 0,
        duration: reducedMotion ? 0 : 0.6,
        delay: reducedMotion ? 0 : index * 0.07,
        ease: 'power3.out',
      }
    );

    return () => entranceAnimRef.current?.kill();
  }, [index, isActive, reducedMotion]);

  useEffect(() => () => {
    document.body.style.cursor = 'auto';
  }, []);

  const handleViewProject = (event) => {
    if (event) {
      event.stopPropagation();
    }
    if (!isActive || !project.url) return;

    const button = buttonRef.current;
    if (!reducedMotion && button) {
      gsap.fromTo(button.scale, { x: 0.94, y: 0.94 }, {
        x: 1,
        y: 1,
        duration: 0.12,
        ease: 'power2.out',
      });
    }

    window.open(project.url, '_blank', 'noopener,noreferrer');
  };

  const handleTileClick = (event) => {
    if (!isActive) return;

    if (!isPhone) {
      onClick();
      return;
    }

    const start = pointerStartRef.current;
    const distance = start
      ? Math.hypot(event.clientX - start.x, event.clientY - start.y)
      : 0;

    if (distance <= 8) onClick();
  };

  return (
    <group
      position={position}
      scale={scale}
      rotation={rotation}
      onPointerDown={(event) => {
        if (!isActive) return;
        pointerStartRef.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={handleTileClick}
      onPointerOver={(event) => {
        if (!isActive) return;
        event.stopPropagation();
        if (!isPhone) {
          document.body.style.cursor = 'pointer';
          setHovered(true);
        }
      }}
      onPointerOut={(event) => {
        if (!isActive) return;
        event.stopPropagation();
        if (!isPhone) {
          document.body.style.cursor = 'auto';
          setHovered(false);
        }
      }}
    >
      <group ref={projectRef}>
        {/* Invisible expanded hit area box so cursor hover is never lost when moving across expanded card */}
        <mesh ref={hitBoxRef} position={[0, 0, 0]}>
          <boxGeometry args={[4.8, 2.5, 0.3]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>

        <mesh ref={shadowRef} position={[0.08, -0.08, -0.08]}>
          <boxGeometry args={[4.62, 2.42, 0.04]} />
          <meshBasicMaterial color={INK} transparent opacity={0.12} />
        </mesh>

        <mesh ref={cardRef}>
          <boxGeometry args={[4.6, 2.4, 0.1]} />
          <meshBasicMaterial
            color={PAPER}
            transparent
            opacity={isPhone ? (dimmed ? 0.78 : 1) : 0.96}
          />
          <Edges color={ACCENT} lineWidth={isPhone ? 1.5 : 1.25} threshold={15} />
        </mesh>

        <mesh ref={accentRef} position={[-2.22, 0, 0.07]}>
          <planeGeometry args={[0.08, 2.16]} />
          <meshBasicMaterial color={ACCENT} />
        </mesh>

        <group ref={metaRef} position={[0, 0.82, 0.075]}>
          <Text
            {...bodyProps}
            position={[-2.02, 0.08, 0]}
            fontSize={isPhone ? 0.17 : 0.18}
            letterSpacing={0.08}
          >
            {(project.category || 'FEATURED PROJECT').toUpperCase()}
          </Text>
          <Text
            {...bodyProps}
            position={[0.2, 0.08, 0]}
            fontSize={isPhone ? 0.16 : 0.18}
            color={MUTED}
          >
            {project.date.toUpperCase()}
          </Text>
          <Text
            {...bodyProps}
            position={[1.88, 0.1, 0]}
            fontSize={isPhone ? 0.2 : 0.22}
            color={ACCENT}
          >
            {String(index + 1).padStart(2, '0')}
          </Text>
        </group>

        <Text
          ref={titleRef}
          {...titleProps}
          position={[-2.02, -0.62, 0.08]}
          anchorY="bottom"
          maxWidth={3.95}
          fontSize={isPhone ? 0.64 : 0.62}
          lineHeight={0.88}
        >
          {project.title}
        </Text>

        <group ref={detailsRef} position={[0, 0.25, 0.08]} visible={false}>
          <mesh position={[0, 0.38, 0]}>
            <planeGeometry args={[4.04, 0.015]} />
            <meshBasicMaterial color={ACCENT} transparent opacity={0.35} />
          </mesh>

          <Text
            {...bodyProps}
            position={[-2.02, 0.20, 0.01]}
            maxWidth={3.9}
            fontSize={isPhone ? 0.19 : 0.19}
            lineHeight={1.35}
            color={INK}
          >
            {project.subtext}
          </Text>

          <group position={[-2.02, isPhone ? -1.1 : -1.05, 0.01]}>
            {tags.map((tag, tagIndex) => {
              const previousWidth = tags
                .slice(0, tagIndex)
                .reduce((total, item) => total + Math.min(1.15, 0.38 + item.length * 0.07) + 0.12, 0);
              const width = Math.min(1.15, 0.38 + tag.length * 0.07);

              return (
                <group key={tag} position={[previousWidth, 0, 0]}>
                  <mesh position={[width / 2, 0, 0]}>
                    <planeGeometry args={[width, 0.32]} />
                    <meshBasicMaterial color={ACCENT} transparent opacity={0.12} />
                    <Edges color={ACCENT} lineWidth={0.75} />
                  </mesh>
                  <Text
                    {...bodyProps}
                    position={[0.12, 0.07, 0.01]}
                    fontSize={isPhone ? 0.16 : 0.14}
                    color={ACCENT}
                  >
                    {tag.toUpperCase()}
                  </Text>
                </group>
              );
            })}
          </group>
        </group>

        <group
          ref={buttonRef}
          position={[1.25, -0.74, -0.4]}
          scale={[0, 0, 1]}
          onPointerDown={(event) => {
            if (!isActive) return;
            event.stopPropagation();
          }}
          onClick={(event) => {
            if (!isActive) return;
            event.stopPropagation();
            handleViewProject(event);
          }}
          onPointerOver={(event) => {
            if (!isActive) return;
            event.stopPropagation();
            document.body.style.cursor = 'pointer';
          }}
          onPointerOut={(event) => {
            if (!isActive) return;
            event.stopPropagation();
            document.body.style.cursor = 'auto';
          }}
        >
          <mesh>
            <boxGeometry args={isPhone ? [1.9, 0.82, 0.12] : [1.48, 0.46, 0.12]} />
            <meshBasicMaterial color={ACCENT} />
          </mesh>
          <Text
            {...bodyProps}
            position={[isPhone ? -0.75 : -0.56, isPhone ? 0.13 : 0.11, 0.08]}
            fontSize={isPhone ? 0.18 : 0.155}
            color={PAPER}
            letterSpacing={0.04}
          >
            VIEW PROJECT  ↗
          </Text>
        </group>
      </group>
    </group>
  );
};

export default ProjectTile;
